import { RawDraftContentState } from 'draft-js'
import { IClientInfo } from './client'
import { INote } from './notes'

export enum CommentaryKind {
  LCR = 'LCR',
  AmendmentAnalysis = 'AMENDMENT_ANALYSIS',
  OpsWriteup = 'OPS_WRITEUP',
}

export type ReorderDefaultCommentaryParams = {
  fieldId: string
  newOrder: number
}
export type ReorderDefaultCommentarySectionParams = {
  sectionId: string
  newOrder: number
  tab: string
}

export interface SectionDragItem {
  type: string
  id: string
  index: number
  tab: string
}
export interface DragItem {
  index: number
  id: string
  type: string
  field: ICommentary | IDefaultCommentary
  sectionId: string
}

export interface IDefaultCommentarySection {
  id: string
  name: string
  tab: string
  exhibit: string
  aliases: string[]
  order: number
  commentaryFields: IDefaultCommentary[]
  commentaries?: ICommentary[]
  deletedAt?: string | null
}

export interface IDefaultCommentary {
  id: string
  sectionId: string
  field: string
  helperText: string | null
  defaultText: RawDraftContentState | null
  order: number
  section: IDefaultCommentarySection
  createdAt: string
  updatedAt: string
}

/**
 * Supporting file attached to a commentary. 1:N — every commentary may
 * carry zero or more uploaded files in Box. Only populated for
 * AmendmentAnalysis commentaries today; the upload endpoint gates on the
 * section kind so LCR commentaries can't have files attached.
 */
export interface ICommentaryFile {
  id: string
  commentaryId: string
  boxFileId: string
  boxFileName: string
  createdAt: string
  updatedAt: string
}

export interface ICommentary {
  id: string
  clientName: string
  clientInfo?: IClientInfo
  generalAnalysisId?: string | null
  sectionId: string
  section?: {
    id: string
    name: string
    tab?: string
    exhibit?: string | null
    order?: number
    /**
     * Which surface the section belongs to. Joined wherever the consumer has to
     * tell the surfaces apart — notification routing picks the LCR link off
     * this rather than guessing from which owner columns the note carries.
     */
    kind?: CommentaryKind
  }
  field: string
  helperText: string | null
  /**
   * Set only on an exhibit comment-thread anchor — the exhibit acts as the
   * commentary field and this row carries its replies. Never rendered as a box.
   */
  exhibit?: string | null
  /**
   * User-arranged layout: zero-based row index within the box's zone.
   * null = never part of a saved arrangement (legacy hard-coded layout).
   */
  layoutRow?: number | null
  /**
   * User-arranged layout: exhibit key this box renders under (per-analysis
   * override of section.exhibit), or null for the tab's general area.
   * Only meaningful when layoutRow is set.
   */
  layoutExhibit?: string | null
  files?: ICommentaryFile[]
  createdAt: string
  updatedAt: string
  note?: INote
}

/**
 * One visual row of the drag-arranged layout: which zone it belongs to
 * (an exhibit key, or the tab's general area when absent) and the
 * commentary ids in left-to-right order. Sent to the save-layout endpoint
 * in visual order.
 */
export interface ICommentaryLayoutRow {
  exhibit?: string | null
  boxes: string[]
}
